import React, {
  useMemo,
} from 'react';
import BaseDialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import Button from 'pages/Dashboard/components/Button';
import {
  getSubmitButtonText,
  notEmptyString,
} from '@populateui/utils';
import {
  useGetUserManagementSettings,
  useUpsertUser,
} from 'views/EMR/Settings/hooks';
import {
  UserTitleOptionDTO,
  CreateUserRequestDTO,
} from 'dtos';
import {
  normalizePhoneNumber,
  validateEmail,
  validatePhoneIfSet,
} from 'pages/Dashboard/utils/helper';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import PhoneInput from 'core/components/PhoneInput';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useNotify,
} from 'core/hooks';
import Alert from 'pages/Dashboard/components/Alert';
import clsx from 'clsx';
import {
  UserView,
} from 'views/EMR/Settings/Users';
import ChevronDownIcon from 'core/icons/chevronDown';
import AutoComplete from 'core/components/AutoComplete';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Tooltip from 'core/components/Tooltip';
import {
  Link,
} from 'react-router-dom';
import {
  helpDeskURL,
} from 'core/constants';

export type UserRequest = CreateUserRequestDTO & {
  userId?: number;
};

export type UserDialogParams = {
  data?: UserView;
};

type Props = UserDialogParams & {
  onClose: () => void;
};

function ServiceAccountAlertContent() {
  return (
    <p className="text-center">
      This is a system account and cannot be edited. Please
      {' '}
      <Link to={helpDeskURL} target="_blank" className="underline">contact support</Link>
      {' '}
      for assistance.
    </p>
  );
}

export default function Dialog({
  data,
  onClose,
}: Props) {
  const isNew = isNil(data);
  const isDoctor = !isNew && /doctor/i.test(data?.userTitle?.title ?? '');
  const isServiceAccount = !isNew && data?.isServiceAccount;

  const notify = useNotify();
  const { mutateAsync: upsertUser } = useUpsertUser(isNew);
  const { data: userSettings } = useGetUserManagementSettings();
  const { userTitles } = userSettings ?? {};

  const usableUserTitles = useMemo(
    () => userTitles?.filter((title) => !title.isServiceAccount),
    [userTitles],
  );

  const formProps = useForm<UserView>({
    mode: 'onChange',
    defaultValues: data ?? undefined,
  });
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = formProps;

  const onCreate = async ({
    userTitle,
    phoneNumber,
    credentials,
    designation,
    ...data
  }: UserView) => {
    const payload = {
      ...data,
      phoneNumber: normalizePhoneNumber(phoneNumber ?? '') ?? undefined,
      title: userTitle?.title ?? '',
      Credential: notEmptyString(credentials) ? credentials : undefined,
      Designation: notEmptyString(designation) ? designation : undefined,
    };
    try {
      await upsertUser(payload);
      const message = isNew
        ? 'The account has been successfully created. Please ask the user to check their email for the login credentials.'
        : 'The user updated successfully.';
      notify({
        message,
        variant: 'success',
      });
      settingsEventChannel.emit('onUsersChange');
      onClose?.();
    } catch (e) {
      console.error(e);
    }
  };

  const submitButtonText = useMemo(() => getSubmitButtonText(
    isNew,
    isSubmitting,
  ), [isNew, isSubmitting]);

  return (
    <BaseDialog
      title={(
        <div className="flex flex-1 flex-col space-y-2">
          <h2 className="text-lg font-semibold">
            {isDoctor ? 'User information' : `${isNew ? 'Create' : 'Edit'} User`}
          </h2>
        </div>
      )}
      closeBtnClasses="!-top-1"
      onClose={onClose}
      onConfirm={noop}
      className="!w-[42rem] populate-form"
      maxWidth="lg"
      open
    >
      <Alert
        open
        severity="info"
        icon
        className="flex justify-center !mt-2 !text-xs"
      >
        {isServiceAccount
          ? <ServiceAccountAlertContent />
          : (
            <>
              For any provider-related requests, please
              {' '}
              <Link to={helpDeskURL} target="_blank" className="underline">
                contact support
              </Link>
              {' '}
              directly.
            </>
          )}
      </Alert>
      <div className="flex flex-1 flex-col gap-form-row">
        <FormProvider {...formProps}>
          <div className="flex gap-form-field">
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  error={!isNil(error)}
                  showErrorText={false}
                  className="w-full text-gray-500 text-xs"
                  id="first-name"
                  label="First Name"
                  placeholder="First Name"
                  inputProps={{ maxLength: 64 }}
                  required
                  disabled={isDoctor || isServiceAccount}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{ required: 'Last Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  error={!isNil(error)}
                  showErrorText={false}
                  className="w-full text-gray-500 text-xs"
                  id="last-name"
                  label="Last Name"
                  placeholder="Last Name"
                  inputProps={{ maxLength: 64 }}
                  required
                  disabled={isDoctor || isServiceAccount}
                />
              )}
            />
          </div>
          <div className="flex gap-form-field">
            <Controller
              name="userTitle"
              control={control}
              rules={{ required: 'Title is required' }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="user-title"
                  label="Title"
                  className="w-full"
                  error={!isNil(error)}
                  showErrorText={false}
                  required
                >
                  <AutoComplete<UserTitleOptionDTO>
                    {...field}
                    className={clsx('w-full text-gray-500 text-xs')}
                    placeholder="Select"
                    id="user-title"
                    options={usableUserTitles ?? []}
                    getLabel={(option) => option?.title ?? ''}
                    openOnFocus
                    dropdownClassName="!p-0"
                    classes={{
                      inputRoot: '!pr-2 !py-2',
                      input: '!mr-4 !truncate',
                      popupIndicatorOpen: '[&>svg]:rotate-180',
                    }}
                    InputProps={{
                      classes: {
                        input: 'populate-input-placeholder',
                      },
                    }}
                    popupIcon={<ChevronDownIcon className={clsx('text-gray-600 -mt-1', { '!hidden': !isNew })} />}
                    disabled={!isNew || isServiceAccount}
                    getOptionDisabled={(option) => option.isRestricted ?? false}
                    renderOption={(props, option) => (
                      <Tooltip
                        hide={!option.isRestricted}
                        title="Restricted. Contact populate support."
                        placement="top-start"
                      >
                        <li
                          {...props}
                          onClick={(event) => {
                            if (!option.isRestricted) {
                              props.onClick?.(event);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (!option.isRestricted) {
                              props.onKeyDown?.(event);
                            }
                          }}
                          role="option"
                          aria-selected={props['aria-selected']}
                          className={clsx(props.className, { '!pointer-events-auto': option.isRestricted })}
                        >
                          {option.title}
                        </li>
                      </Tooltip>
                    )}
                  />
                </Wrapper>
              )}
            />
            <Controller
              name="credentials"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  showErrorText={false}
                  className="w-full text-gray-500 text-xs"
                  id="credentials-name"
                  label="Credentials"
                  placeholder="E.g. DPM"
                  inputProps={{ maxLength: 8 }}
                  disabled={isDoctor || isServiceAccount}
                />
              )}
            />
            <Controller
              name="designation"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  className="w-full text-gray-500 text-xs"
                  id="designation"
                  label="Designation"
                  placeholder="Designation"
                  inputProps={{ maxLength: 64 }}
                  disabled={isDoctor || isServiceAccount}
                />
              )}
            />
          </div>
          <div className="flex gap-form-field">
            <Controller
              name="email"
              control={control}
              rules={{ validate: validateEmail, required: 'Email is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  error={!isNil(error)}
                  showErrorText={false}
                  className="w-full"
                  id="email"
                  label="Email"
                  type="email"
                  placeholder="Email"
                  disabled={!isNew || isServiceAccount}
                  required
                />
              )}
            />
            <Controller
              name="phoneNumber"
              control={control}
              rules={{ validate: validatePhoneIfSet }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="phone-number"
                  error={!isNil(error)}
                  showErrorText={false}
                  label="Phone"
                  className="w-full"
                  containerClassName="w-full"
                >
                  <PhoneInput
                    {...field}
                    error={!isNil(error)}
                    id="phone-number"
                    name="phone-number"
                    placeholder="Phone"
                    disabled={isDoctor || isServiceAccount}
                  />
                </Wrapper>
              )}
            />
          </div>
          <div className="flex flex-row-reverse justify-between gap-4">
            {!isDoctor && !isServiceAccount && (
            <DisabledFormTooltip>
              <Button
                className="!py-1.5"
                variant="contained"
                type="submit"
                color="primary"
                onClick={handleSubmit(onCreate)}
                disabled={!isValid || isSubmitting}
              >
                {submitButtonText}
              </Button>
            </DisabledFormTooltip>
            )}
            <Button
              className="!py-1.5"
              variant="outlined"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </div>
        </FormProvider>
      </div>
    </BaseDialog>
  );
}
